import { useEffect, useState } from 'react';

import { Constants } from '../common/Constants';
import { getIsSuperUser } from '../common/Widget/SmartChoiceWidget/superUserUtils';
import { useSaveCareTeam } from '../hooks/useCareTeam/useCareTeam';
import { useCurrentMember } from '../hooks/useCurrentMember';
import {
  useEducationalContentStatus,
  useSaveEducationalContentStatus,
} from '../hooks/useEducationalContent/useEducationalContent';
import { useHsidUuid } from '../hooks/useHsidUuid';
import { useLagoon } from '../hooks/useLagoon';
import { useDeletePrimaryCare } from '../hooks/usePrimaryCare';
import { useNonGatedPcpStore } from '../store/useNonGatedPcpStore';
import { isPcpSuppressionEligible } from './pcpSuppression.utils';

export interface RemoveCareTeamProviderParams {
  providerId: string;
  isPcp: boolean;
  onSuccess?: () => void;
  onCompleted?: () => void;
}

export interface RemoveCareTeamProviderResult {
  removeCareTeamProvider: (params: RemoveCareTeamProviderParams) => void;
  isLoading: boolean;
}

export const useRemoveCareTeamProvider = (): RemoveCareTeamProviderResult => {
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all global data inside the hook
  const currentMember = useCurrentMember();
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const hsidUuid = useHsidUuid();
  const allConfigs = useLagoon('config');
  const isSuperUser = getIsSuperUser(allConfigs);
  const { clearNonGatedPrimaryCareData, setHasFetchedNonGatedPrimaryCareData } =
    useNonGatedPcpStore();

  const [educationalContent, getEducationalContentStatus] =
    useEducationalContentStatus({});
  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({});

  // Trigger the query so the banner status is available before removal runs
  useEffect(() => {
    getEducationalContentStatus({
      variables: {
        isSuperUser,
        ...(hsidUuid && { userId: hsidUuid }),
      },
    });
  }, [hsidUuid, isSuperUser]);

  const [, deletePrimaryCare] = useDeletePrimaryCare({
    onCompleted: (_data: unknown) => {
      setIsLoading(false);
    },
  });

  const [, removeCareTeam] = useSaveCareTeam({
    onCompleted: (_data: unknown) => {
      setIsLoading(false);
    },
  });

  const removeCareTeamProvider = (params: RemoveCareTeamProviderParams) => {
    const { providerId, isPcp, onSuccess, onCompleted } = params;

    const enableNonGatedPcpSuppression =
      featureFlags.find(
        (flag) => flag.key === 'ENABLE_NON_GATED_PCP_SUPPRESSION'
      )?.active ?? false;
    const isPcpSuppressed = isPcpSuppressionEligible(
      currentMember,
      enableNonGatedPcpSuppression
    );

    setIsLoading(true);

    // For non-gated PCP members, use deletePCP API
    if (isPcpSuppressed && isPcp) {
      deletePrimaryCare({
        variables: {
          familyId: (currentMember as { familyId?: string })?.familyId,
          memberId: (
            currentMember as {
              demographics?: { coverageID?: Array<{ id?: string }> };
            }
          )?.demographics?.coverageID?.[0]?.id,
          dependentSeqNbr: (
            currentMember as { demographics?: { dependentSeqNbr?: string } }
          )?.demographics?.dependentSeqNbr,
          isPcpNotRequired: (currentMember as { isPcpNotRequired?: boolean })
            ?.isPcpNotRequired,
        },
      })
        .then((response) => {
          setIsLoading(false);
          onCompleted?.();
          if (
            (response as { data?: { deletePCP?: { success?: boolean } } })?.data
              ?.deletePCP?.success
          ) {
            // Clear non-gated PCP store after successful deletion
            clearNonGatedPrimaryCareData();
            setHasFetchedNonGatedPrimaryCareData(true);

            onSuccess?.();

            // Update educational content status
            const hasViewedBanner =
              educationalContent?.data?.educationalContentStatus
                ?.hasViewedNonGatedPcpSuppressionBanner;
            if (!hasViewedBanner) {
              setEducationalContentStatus({
                variables: {
                  hasViewedNonGatedPcpSuppressionBanner: true,
                  hasViewedContent:
                    educationalContent?.data?.educationalContentStatus
                      ?.hasViewedContent,
                  ...(hsidUuid && { userId: hsidUuid }),
                  isSuperUser,
                },
              });
            }
          }
        })
        .catch(() => {
          setIsLoading(false);
          onCompleted?.();
        });
    } else {
      // Regular care team removal
      removeCareTeam({
        variables: {
          groupNumber:
            (currentMember as { policyNumber?: string })?.policyNumber || '',
          providerId,
          isSuperUser,
          isUnsave: true,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      })
        .then((response) => {
          setIsLoading(false);
          onCompleted?.();
          const status = (
            response as { data?: { saveCareTeam?: { status?: string } } }
          )?.data?.saveCareTeam?.status;
          if (status === '200') {
            onSuccess?.();
          }
        })
        .catch(() => {
          setIsLoading(false);
          onCompleted?.();
        });
    }
  };

  return {
    removeCareTeamProvider,
    isLoading,
  };
};
